//
//  Signals.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 24, 2026
//  Author: Bob Keathley
//
//  Off-Course Signals list (§6u): filters, cursor paging, and a Create signal
//  form for team-admin and above (source "person").
//
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { api, getAssumed, subscribeAssumed } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import type { ProjectPage, ProjectSummary } from '@shared/api/projects'
import {
  SIGNAL_SOURCES, SIGNAL_STATUSES, SIGNALS_EMPTY, canTriageSignals, parseDeedRefs, signalErrorText, signalsListPath,
  type SignalCreate, type SignalFilters, type SignalPage, type SignalProblem, type SignalSummary,
} from '@shared/api/signals'
import type { Session } from '@shared/auth'
import { href, navigate } from '@shared/router'

const when = (s: number) => new Date(s * 1000).toLocaleString()

export type Prefill = { session?: string; agent?: string; deeds?: string }

const blank: SignalProblem = { what: '', correction: '', corrected_by: 'person' }

function CreateSignal({ session, projects, prefill, onDone }: { session: Session; projects: ProjectSummary[]; prefill: Prefill; onDone: () => void }) {
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '')
  const [agent, setAgent] = useState(prefill.agent ?? '')
  const [sessionId, setSessionId] = useState(prefill.session ?? '')
  const [summary, setSummary] = useState('')
  const [problems, setProblems] = useState<SignalProblem[]>([{ ...blank }])
  const [deedText, setDeedText] = useState(prefill.deeds ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const setProblem = (i: number, p: Partial<SignalProblem>) => setProblems((prev) => prev.map((x, j) => (j === i ? { ...x, ...p } : x)))

  async function submit() {
    setErr(null)
    const deed_refs = parseDeedRefs(deedText)
    if (deed_refs === null) { setErr('Deed refs are one per line as notebook-id:seq.'); return }
    const body: SignalCreate = { project_id: projectId, agent: agent.trim(), session_id: sessionId.trim(), source: 'person', summary: summary.trim(), problems, deed_refs }
    setBusy(true)
    const r = await api<{ id: string }>('/signals', { method: 'POST', body: JSON.stringify(body), headers: { 'Idempotency-Key': crypto.randomUUID() } }, session.token)
    setBusy(false)
    if (!isOk(r)) { setErr(signalErrorText(r.error)); return }
    onDone()
    navigate(`/signals/${r.data.id}`)
  }

  return (
    <div className="panel">
      <h3>Create signal</h3>
      <p className="hint">Flag a problem a person corrected in a run. It is a record only: it changes no Notebook, release or mandate.</p>
      <label htmlFor="sig-project">Project</label>
      <select id="sig-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <label htmlFor="sig-agent">Agent</label>
      <input id="sig-agent" value={agent} maxLength={64} onChange={(e) => setAgent(e.target.value)} />
      <label htmlFor="sig-session">Run (session id)</label>
      <input id="sig-session" value={sessionId} maxLength={200} onChange={(e) => setSessionId(e.target.value)} />
      <label htmlFor="sig-summary">Summary (one line)</label>
      <input id="sig-summary" value={summary} maxLength={300} onChange={(e) => setSummary(e.target.value.replace(/[\r\n]/g, ' '))} />
      {problems.map((p, i) => (
        <div key={i} className="problem">
          <label htmlFor={`sig-what-${i}`}>Problem {i + 1}: what happened</label>
          <textarea id={`sig-what-${i}`} value={p.what} maxLength={2000} onChange={(e) => setProblem(i, { what: e.target.value })} />
          <label htmlFor={`sig-fix-${i}`}>The correction</label>
          <textarea id={`sig-fix-${i}`} value={p.correction} maxLength={2000} onChange={(e) => setProblem(i, { correction: e.target.value })} />
          <label htmlFor={`sig-by-${i}`}>Corrected by</label>
          <select id={`sig-by-${i}`} value={p.corrected_by} onChange={(e) => setProblem(i, { corrected_by: e.target.value as SignalProblem['corrected_by'] })}>
            <option value="person">person</option><option value="agent">agent</option><option value="gate">gate</option>
          </select>
          {problems.length > 1 && <p><button className="btn ghost sm" onClick={() => setProblems((prev) => prev.filter((_, j) => j !== i))}>Remove problem</button></p>}
        </div>
      ))}
      {problems.length < 20 && <p><button className="btn ghost sm" onClick={() => setProblems((prev) => [...prev, { ...blank }])}>Add another problem</button></p>}
      <label htmlFor="sig-deeds">Deeds pointed at (optional, notebook-id:seq per line)</label>
      <textarea id="sig-deeds" value={deedText} onChange={(e) => setDeedText(e.target.value)} />
      {err && <p className="err" role="alert">{err}</p>}
      <p><button className="btn" disabled={busy || !projectId} onClick={submit}>{busy ? 'Creating…' : 'Create signal'}</button></p>
    </div>
  )
}

export default function Signals({ session, prefill = {} }: { session: Session; prefill?: Prefill }) {
  const assumed = useSyncExternalStore(subscribeAssumed, getAssumed, getAssumed)
  const canCreate = canTriageSignals(session.user.role)
  const readOnly = assumed?.mode === 'read'
  const [filters, setFilters] = useState<SignalFilters>({})
  const [items, setItems] = useState<SignalSummary[] | null>(null)
  const [next, setNext] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [projects, setProjects] = useState<ProjectSummary[]>([])
  const [creating, setCreating] = useState(!!(prefill.session || prefill.agent))

  const load = useCallback(async () => {
    setItems(null)
    const r = await api<SignalPage>(signalsListPath(filters), {}, session.token)
    if (isOk(r)) { setItems(r.data.items); setNext(r.data.next_cursor); setError(null) } else { setItems([]); setNext(null); setError(signalErrorText(r.error)) }
  }, [filters, session.token])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    api<ProjectPage<ProjectSummary>>('/projects', {}, session.token).then((r) => { if (isOk(r)) setProjects(r.data.items) })
  }, [session.token])

  async function more(cursor: string) {
    setBusy(true)
    const r = await api<SignalPage>(signalsListPath(filters, cursor), {}, session.token)
    setBusy(false)
    if (!isOk(r)) { setError(signalErrorText(r.error)); return }
    setItems((prev) => [...(prev ?? []), ...r.data.items])
    setNext(r.data.next_cursor)
  }

  const set = (k: keyof SignalFilters, v: string) => setFilters((f) => ({ ...f, [k]: v || undefined }))
  const agents = [...new Set((items ?? []).map((s) => s.agent))].sort()

  return (
    <div className="signals">
      <h2>Signals</h2>
      <p className="hint">Problems an agent session had that were corrected, by a person, another agent or a gate. A signal is a record only.</p>
      {canCreate && (
        <p>
          <button className="btn" disabled={readOnly || projects.length === 0} title={readOnly ? 'This is an assumed read-only session; it cannot create signals.' : projects.length === 0 ? 'No projects yet.' : undefined} onClick={() => setCreating((v) => !v)}>
            {creating ? 'Close' : 'Create signal'}
          </button>
        </p>
      )}
      {canCreate && creating && projects.length > 0 && <CreateSignal session={session} projects={projects} prefill={prefill} onDone={() => { setCreating(false); load() }} />}
      <div className="filters">
        <div>
          <label htmlFor="f-project">Project</label>
          <select id="f-project" value={filters.project ?? ''} onChange={(e) => set('project', e.target.value)}>
            <option value="">All</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="f-agent">Agent</label>
          <input id="f-agent" list="signal-agents" value={filters.agent ?? ''} onChange={(e) => set('agent', e.target.value.trim())} />
          <datalist id="signal-agents">{agents.map((a) => <option key={a} value={a} />)}</datalist>
        </div>
        <div>
          <label htmlFor="f-status">Status</label>
          <select id="f-status" value={filters.status ?? ''} onChange={(e) => set('status', e.target.value)}>
            <option value="">All</option>
            {SIGNAL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="f-source">Source</label>
          <select id="f-source" value={filters.source ?? ''} onChange={(e) => set('source', e.target.value)}>
            <option value="">All</option>
            {SIGNAL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      {error && <p className="err" role="alert">{error}</p>}
      <table>
        <thead><tr><th>ID</th><th>When</th><th>Project</th><th>Agent</th><th>Source</th><th>Summary</th><th>Status</th></tr></thead>
        <tbody>
          {items === null && <tr><td colSpan={7} className="hint">Loading…</td></tr>}
          {items && items.length === 0 && !error && <tr><td colSpan={7} className="hint">{SIGNALS_EMPTY}</td></tr>}
          {items?.map((s) => (
            <tr key={s.id}>
              <td className="mono"><a href={href(`/signals/${s.id}`)}>{s.id}</a></td>
              <td className="hint">{when(s.created_at)}</td>
              <td>{s.project_name}</td>
              <td className="mono">{s.agent}</td>
              <td><span className="pill">{s.source}</span></td>
              <td>{s.summary}{s.problem_count > 1 && <span className="hint"> · {s.problem_count} problems</span>}</td>
              <td><span className={`pill st-${s.status}`}>{s.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {next !== null && <p><button className="btn ghost" disabled={busy} onClick={() => more(next)}>{busy ? 'Loading…' : 'Load more'}</button></p>}
    </div>
  )
}
